import type {
  PayoutRateCalculations,
  ChargeHoursParams,
  PayHoursParams,
  MarginCalculations,
} from './rateSettingsCalculations.types';

const calculateRegularHours = (hours: number, maxRegularHours: number): number => {
  return Math.min(hours, maxRegularHours);
};

const calculateOvertimeHours = (
  hours: number,
  maxRegularHours: number,
  overtimeMultiplier: number,
): number => {
  return Math.max(hours - maxRegularHours, 0) * overtimeMultiplier;
};

const calculateDoubleOvertimeHours = (
  hours: number,
  maxRegularHours: number,
  maxOtHours: number,
  doubleOvertimeMultiplier: number,
): number => {
  return Math.max(hours - maxRegularHours - maxOtHours, 0) * doubleOvertimeMultiplier;
};

export const calcChargeHours = ({
  hours,
  charge_max_daily_regular_hours,
  charge_max_daily_ot_hours,
  charge_overtime_multiplier,
  charge_double_overtime_multiplier,
}: ChargeHoursParams): number => {
  const regularHours = calculateRegularHours(hours, charge_max_daily_regular_hours);
  const overtimeHours = calculateOvertimeHours(
    hours,
    charge_max_daily_regular_hours,
    charge_overtime_multiplier,
  );
  const doubleOvertimeHours = calculateDoubleOvertimeHours(
    hours,
    charge_max_daily_regular_hours,
    charge_max_daily_ot_hours,
    charge_double_overtime_multiplier,
  );

  return regularHours + overtimeHours + doubleOvertimeHours;
};

export const calcPayHours = ({
  hours,
  payout_max_daily_regular_hours,
  payout_max_daily_ot_hours,
  payout_overtime_multiplier,
  payout_double_overtime_multiplier,
}: PayHoursParams): number => {
  const regularHours = calculateRegularHours(hours, payout_max_daily_regular_hours);
  const overtimeHours = calculateOvertimeHours(
    hours,
    payout_max_daily_regular_hours,
    payout_overtime_multiplier,
  );
  const doubleOvertimeHours = calculateDoubleOvertimeHours(
    hours,
    payout_max_daily_regular_hours,
    payout_max_daily_ot_hours,
    payout_double_overtime_multiplier,
  );

  return regularHours + overtimeHours + doubleOvertimeHours;
};

export const calcPayoutRate = (params: PayoutRateCalculations) => {
  const {
    chargeRate = 0,
    margin_percent = 0,
    hours = 0,
    charge_max_daily_regular_hours = 0,
    charge_max_daily_ot_hours = 0,
    charge_overtime_multiplier = 0,
    payout_max_daily_regular_hours = 0,
    payout_max_daily_ot_hours = 0,
    payout_overtime_multiplier = 0,
    payout_double_overtime_multiplier = 0,
    charge_double_overtime_multiplier = 0,
    excludeTenCentReduction = false,
  } = params;

  const normalizedMargin = Number(margin_percent || 0);

  const chargeHours = calcChargeHours({
    hours,
    charge_max_daily_regular_hours,
    charge_max_daily_ot_hours,
    charge_overtime_multiplier,
    charge_double_overtime_multiplier,
  });

  const payHours = calcPayHours({
    hours,
    payout_max_daily_regular_hours,
    payout_max_daily_ot_hours,
    payout_overtime_multiplier,
    payout_double_overtime_multiplier,
  });

  if (!payHours) {
    return {
      payoutRate: 0,
      chargeHours,
      payHours,
    };
  }
  const tenCentReduction = excludeTenCentReduction ? 0 : 10;
  const totalPay = chargeHours * (chargeRate * (1 - normalizedMargin) - tenCentReduction);

  return {
    payoutRate: Math.round(totalPay / payHours),
    chargeHours,
    payHours,
  };
};

export const calc1099Margin = (params: MarginCalculations) => {
  const { chargeRate, payoutRate } = params;

  const chargeHours = calcChargeHours({
    hours: params.hours,
    charge_max_daily_regular_hours: params.charge_max_daily_regular_hours,
    charge_max_daily_ot_hours: params.charge_max_daily_ot_hours,
    charge_overtime_multiplier: params.charge_overtime_multiplier,
    charge_double_overtime_multiplier: params.charge_double_overtime_multiplier,
  });

  const payHours = calcPayHours({
    hours: params.hours,
    payout_max_daily_regular_hours: params.payout_max_daily_regular_hours,
    payout_max_daily_ot_hours: params.payout_max_daily_ot_hours,
    payout_overtime_multiplier: params.payout_overtime_multiplier,
    payout_double_overtime_multiplier: params.payout_double_overtime_multiplier,
  });

  const totalCharge = chargeRate * chargeHours;
  const totalPay = (payoutRate - 10) * payHours;

  if (!totalCharge || !totalPay) {
    return 0;
  }

  const margin = 1 - totalPay / totalCharge;
  return parseFloat(margin.toFixed(5));
};

export const calcTotalPay = (params: PayoutRateCalculations) => {
  const { payout_max_daily_regular_hours = 0, payout_overtime_multiplier = 0, hours = 0 } = params;

  const { payoutRate } = calcPayoutRate(params);
  const regularPay = payoutRate * calculateRegularHours(hours, payout_max_daily_regular_hours);
  const overtimePay =
    calculateOvertimeHours(hours, payout_max_daily_regular_hours, payout_overtime_multiplier) *
    payoutRate;

  return regularPay + overtimePay;
};

export const calcTotalCharge = (
  chargeRate: number = 0,
  hours: number = 0,
  charge_max_daily_regular_hours: number = 0,
  charge_overtime_multiplier: number = 0,
) => {
  const regularCharge = chargeRate * calculateRegularHours(hours, charge_max_daily_regular_hours);
  const overtimeCharge =
    calculateOvertimeHours(hours, charge_max_daily_regular_hours, charge_overtime_multiplier) *
    chargeRate;

  return regularCharge + overtimeCharge;
};
